<template>
  <div class="gallery-container">
    <!-- Skip Navigation Link -->
    <a href="#main-content" class="skip-nav" @click="focusMainContent">Skip to main content</a>

    <header class="gallery-header" role="banner">
      <!-- PALLAS watermark removed for cleaner gallery layout -->
      <h1 class="gallery-title" id="gallery-heading">PALLAS Gallery</h1>
      <div class="gallery-search-controls">
        <div class="search-section">
          <form action="#" class="search-form" ref="searchGroup" @click="focusSearchInput" @submit.prevent="onSearchInput">
            <label for="gallery-search" class="visually-hidden">Search PALLAS Gallery</label>
            <div class="search-input-container">
              <div class="filter-icon-section">
                <FilterDropdown ref="filterDropdownRef" @update-filters="handleFilterUpdate" />
              </div>
              <input
                type="text"
                id="gallery-search"
                class="search-input"
                placeholder="Search PALLAS Gallery..."
                v-model="searchQuery"
                @input="onSearchInput"
                ref="searchInput"
                aria-describedby="search-help"
                autocomplete="off" />
              <button class="search-clear-btn" type="button" @click="() => { clearSearch(); resetFilters(); }"
                      aria-label="Clear search and reset filters" v-if="searchQuery">
                ✕
              </button>
            </div>
            <div id="search-help" class="visually-hidden">Search by artist name, bio, or expertise tags</div>
          </form>
        </div>
      </div>

      <!-- Active filters section removed - now handled in FilterDropdown -->

    </header>

    <main id="main-content" class="results" tabindex="-1" ref="mainContent">
      <div id="search-status" aria-live="polite" aria-atomic="true" class="visually-hidden">{{ searchStatusMessage }}</div>

      <transition name="fade">
        <div v-if="loading" class="loading" role="status" aria-label="Loading artists...">
          <span class="visually-hidden">Loading artist profiles...</span>
        </div>
        <div v-else-if="error" class="error-message" role="alert" aria-live="assertive">
          {{ error }}
        </div>
        <div v-else-if="filteredArtists.length === 0" class="no-results" role="status">
          <p>No profiles found matching your criteria.</p>
          <button @click="resetFilters" class="reset-filters-btn">Clear all filters</button>
        </div>
        <section v-else class="artist-grid" aria-labelledby="gallery-heading">
            <article v-for="artist in filteredArtists" :key="`${artist.id}-${artist.visibleAs || artist.role}`"
                 :class="['pallas-artist-card', getRoleBasedBorderClass(artist)]"
                 :aria-labelledby="`artist-name-${artist.id}`"
                 style="display: flex; flex-direction: column;">
              <div class="art-hero-main" :style="getBackgroundImageStyle(artist.profile)">
                <div v-if="(artist.profile.openToCommissions === true) || (artist.profile.openToCollaborations === true)" class="commission-indicator">
                  <span class="commission-dot"></span>
                </div>
                <!-- Dual profile buttons for "both" users -->
                <div v-if="artist.originalRole === 'both' || (artist.visibleAs || artist.role) === 'both'" class="dual-profile-btns">
                  <router-link v-if="artist.artistComplete !== false" :to="{
                    name: 'ProfileNew',
                    params: { uuid: artist.id },
                    query: { searchQuery: searchQuery, selectedFocus: JSON.stringify(selectedFocus), selectedMedium: JSON.stringify(selectedMedium), view: 'artist' }
                  }" class="profile-btn-compact profile-btn-art" :aria-label="`View ${artist.profile.name || 'user'}'s artist profile`">
                    <span class="btn-text">Art</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="icon">
                      <path stroke-linejoin="round" stroke-linecap="round" stroke-miterlimit="10" stroke-width="1.5" d="M8.91016 19.9201L15.4302 13.4001C16.2002 12.6301 16.2002 11.3701 15.4302 10.6001L8.91016 4.08008"></path>
                    </svg>
                  </router-link>
                  <span v-else class="profile-btn-compact profile-btn-art btn-disabled" title="Coming soon">
                    <span class="btn-text">Art</span>
                  </span>
                  <router-link v-if="artist.scientistComplete !== false" :to="{
                    name: 'ProfileNew',
                    params: { uuid: artist.id },
                    query: { searchQuery: searchQuery, selectedFocus: JSON.stringify(selectedFocus), selectedMedium: JSON.stringify(selectedMedium), view: 'scientist' }
                  }" class="profile-btn-compact profile-btn-science" :aria-label="`View ${artist.profile.name || 'user'}'s scientist profile`">
                    <span class="btn-text">Science</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="icon">
                      <path stroke-linejoin="round" stroke-linecap="round" stroke-miterlimit="10" stroke-width="1.5" d="M8.91016 19.9201L15.4302 13.4001C16.2002 12.6301 16.2002 11.3701 15.4302 10.6001L8.91016 4.08008"></path>
                    </svg>
                  </router-link>
                  <span v-else class="profile-btn-compact profile-btn-science btn-disabled" title="Coming soon">
                    <span class="btn-text">Science</span>
                  </span>
                </div>
                <router-link v-else :to="{
                  name: 'ProfileNew',
                  params: { uuid: artist.id },
                  query: {
                    searchQuery: searchQuery,
                    selectedFocus: JSON.stringify(selectedFocus),
                    selectedMedium: JSON.stringify(selectedMedium),
                    view: artist.visibleAs || artist.role
                  }
                }" :class="['profile-btn-compact', getProfileBtnClass(artist)]" :aria-label="`View ${artist.profile.name || 'artist'}'s profile`">
                  <span class="btn-text">Profile</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="icon">
                    <path stroke-linejoin="round" stroke-linecap="round" stroke-miterlimit="10" stroke-width="1.5" d="M8.91016 19.9201L15.4302 13.4001C16.2002 12.6301 16.2002 11.3701 15.4302 10.6001L8.91016 4.08008"></path>
                  </svg>
                </router-link>
              </div>
              <div class="artist-profile-section">
                <div class="profile-image-container">
                  <div :style="getProfileImageStyle(artist.profile)"
                       :aria-label="`Profile photo of ${artist.profile.name || 'artist'}`"
                       role="img"
                       class="profile-image-main" />
                </div>
                <div class="artist-info-main">
                  <div class="name-affiliation-row">
                    <h2 class="artist-name-main" :id="`artist-name-${artist.id}`">{{ artist.profile.name || "No Name Provided" }}</h2>
                    <span class="role-badge" :class="`role-badge-${artist.originalRole || artist.role || 'artist'}`">
                      {{ (artist.originalRole || artist.role) === 'both' ? 'Artist & Scientist' : (artist.originalRole || artist.role) === 'scientist' ? 'Scientist' : 'Artist' }}
                    </span>
                    <span v-if="getAffiliation(artist)" class="card-affiliation" :class="getAffiliationClass(artist)">
                      {{ getAffiliation(artist) }}
                    </span>
                  </div>
                </div>
              </div>
              <!-- Dual-role: all tags in one row, colors distinguish them, larger group first -->
              <div v-if="(artist.visibleAs || artist.role) === 'both'" class="card-tags-section">
                <div class="tag-group-bottom" role="list" aria-label="Expertise areas">
                  <span v-for="tag in getBothTagsSorted(artist)" :key="tag.label" class="card-tag-pill" :data-theme="tag.theme" role="listitem">{{ tag.label }}</span>
                </div>
              </div>
              <!-- Single role: show all tags with individual theming -->
              <div v-else-if="getArtistTags(artist) && getArtistTags(artist).length" class="card-tags-section">
                <div class="tag-group-bottom" role="list" aria-label="Expertise areas">
                  <span v-for="tag in getArtistTags(artist)" :key="tag"
                    :class="['card-tag-pill']"
                    :data-theme="(artist.visibleAs || artist.role) === 'scientist' ? 'science' : (artist.visibleAs || artist.role) === 'artist' ? 'art' : getTagThemeClass(tag).replace('tag-', '')"
                    role="listitem">
                    {{ tag }}
                  </span>
                </div>
              </div>
            </article>
        </section>
      </transition>
    </main>
  </div>
</template>

<script>
const defaultBanner = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 300'%3E%3Cdefs%3E%3Cpattern id='bannerGrid' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 40 0 L 0 0 0 40' fill='none' stroke='%23e5e5e5' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='1200' height='300' fill='%23f8f8f8'/%3E%3Crect width='1200' height='300' fill='url(%23bannerGrid)'/%3E%3C/svg%3E"
const defaultProfile = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23cccccc'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
import { reactive, computed, onMounted, onUnmounted, onActivated, toRefs, ref } from "vue";
import { useRoute } from "vue-router";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import Fuse from "fuse.js";
import FilterDropdown from "./FilterDropdown.vue";
import { useTags } from '@/composables/useTags.js';

export default {
  name: "SearchArtists",
  components: {
    FilterDropdown,
  },
  setup() {
    // Get getTagTheme function from composable
    const { getTagTheme } = useTags();
    const route = useRoute();

    const predefinedFocusTags = [ "Abstract", "Anatomy", "Animals & Wildlife", "Architectural Visualization", "Board & Card Game Art", "Book Illustration", "Character Design", "Children's Art", "Comic Art", "Concept Art", "Data Visualization", "Journal Cover Art", "Editorial Illustration", "Environmental Concept Art & Design", "Fashion & Costume Design", "Game Art", "Gameplay & Level Design", "Games and Real-Time 3D Environment Art", "Graphic Design", "Illustration", "Industrial & Product Design", "Matte Painting", "Mechanical Design", "Motion Graphics", "Photogrammetry & 3D Scanning", "Pixel and Voxel Art", "Portraits", "Realism", "Science Fiction", "Scientific Illustration & Visualization", "Scientific Figure Design", "Sketches", "Technical Art", "Textures & Materials", "Typography", "User Interface (UI) Art", "Vehicles", "VFX for Film, TV & Animation", "VFX for Real-Time & Games", "Virtual and Augmented Reality", "Web Design" ];
    const predefinedMediumTags = [ "Digital 2D", "Digital 3D", "Animation", "Real-time", "Live Action CG/VFX", "3D Printing", "Traditional Ink", "Traditional Dry Media", "Traditional Paint", "Traditional Sculpture", "Mixed Media" ];

    const focusDropdown = ref(null);
    const mediumDropdown = ref(null);
    const searchGroup = ref(null);
    const searchInput = ref(null);
    const mainContent = ref(null);
    const filterDropdownRef = ref(null);

    const state = reactive({
      allArtists: [],      // Master list - loaded once
      loading: true,
      error: null,
      selectedFocus: [],
      selectedMedium: [],
      dropdowns: { focus: false, medium: false },
      searchQuery: "",
      searchFocusQuery: "",
      searchMediumQuery: "",
      sortOption: localStorage.getItem('pallas-sort-option') || "random",
      fuse: null,
      showSortDropdown: false,
      filterOpenCommissions: false,
      filterType: 'both',  // 'artists', 'scientists', or 'both'
      randomSeed: null,    // For stable random sorting
    });


    const sortOptions = ref([
      { value: "random", label: "Random" },
      { value: "name-asc", label: "Name (A-Z)" },
      { value: "name-desc", label: "Name (Z-A)" },
      { value: "date-joined", label: "Date Joined" }
    ]);

    const appliedFilters = computed(() => {
      const focusFilters = Array.isArray(state.selectedFocus)
        ? state.selectedFocus.map((tag) => ({ type: "focus", tag }))
        : [];
      const mediumFilters = Array.isArray(state.selectedMedium)
        ? state.selectedMedium.map((tag) => ({ type: "medium", tag }))
        : [];
      return [...focusFilters, ...mediumFilters];
    });

    // Accessibility computed properties
    const searchStatusMessage = computed(() => {
      if (state.loading) return 'Loading artist profiles...';
      if (state.error) return `Error: ${state.error}`;
      if (filteredArtists.value.length === 0) return 'No profiles found matching your criteria.';
      const count = filteredArtists.value.length;
      const searchText = state.searchQuery ? ` for "${state.searchQuery}"` : '';
      return `Found ${count} artist${count === 1 ? '' : 's'}${searchText}`;
    });

    // CENTRALIZED FILTERING - All filtering happens here, no re-fetching
    const filteredArtists = computed(() => {
      // Start with the master list
      let result = state.allArtists;

      // A. Search Filter (using Fuse if query exists)
      if (state.searchQuery.trim() !== "" && state.fuse) {
        const fuseResults = state.fuse.search(state.searchQuery.trim());
        result = fuseResults.map((r) => r.item);
      }

      // B. Role Type Filter
      if (state.filterType === 'artists') {
        result = result.filter(a => a.visibleAs === 'artist');
      } else if (state.filterType === 'scientists') {
        result = result.filter(a => a.visibleAs === 'scientist');
      } else {
        // "both" — show one card per person, prefer 'both' entry, dedupe artist/scientist entries
        const seen = new Set();
        result = result.filter(a => {
          if (a.visibleAs === 'both') {
            seen.add(a.id);
            return true;
          }
          // Only show artist/scientist entry if there's no 'both' entry for this user
          if (seen.has(a.id)) return false;
          // Check if a 'both' entry exists for this user
          const hasBothEntry = result.some(x => x.id === a.id && x.visibleAs === 'both');
          if (hasBothEntry) return false;
          return true;
        });
      }

      // C. Tag Filters (case-insensitive comparison)
      if (state.selectedFocus.length > 0 || state.selectedMedium.length > 0) {
        result = result.filter((artist) => {
          const artistTags = (artist.tagsSelected || []).map(t => t.toLowerCase());

          let matchesFocus = true;
          let matchesMedium = true;

          if (state.selectedFocus.length > 0) {
            matchesFocus = state.selectedFocus.every(tag =>
              artistTags.includes(tag.toLowerCase())
            );
          }

          if (state.selectedMedium.length > 0) {
            matchesMedium = state.selectedMedium.every(tag =>
              artistTags.includes(tag.toLowerCase())
            );
          }
          return matchesFocus && matchesMedium;
        });
      }

      // C. Commissions Filter
      if (state.filterOpenCommissions) {
        result = result.filter(artist => artist.profile.openToCommissions === true);
      }

      // D. Sorting (Create a copy to avoid mutating)
      let sorted = [...result];
      switch (state.sortOption) {
        case "name-asc":
          sorted.sort((a, b) => {
            const nameA = a.profile.name ? a.profile.name.toLowerCase() : "";
            const nameB = b.profile.name ? b.profile.name.toLowerCase() : "";
            return nameA.localeCompare(nameB);
          });
          break;
        case "name-desc":
          sorted.sort((a, b) => {
            const nameA = a.profile.name ? a.profile.name.toLowerCase() : "";
            const nameB = b.profile.name ? b.profile.name.toLowerCase() : "";
            return nameB.localeCompare(nameA);
          });
          break;
        case "date-joined":
          sorted.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.profile.createdAt || 0);
            const dateB = new Date(b.createdAt || b.profile.createdAt || 0);
            return dateB - dateA;
          });
          break;
        case "random":
        default:
          // Use seeded random for stable sorting (only shuffles on page load)
          if (state.randomSeed) {
            sorted.sort((a, b) => {
              const hashA = (a.id + state.randomSeed).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
              const hashB = (b.id + state.randomSeed).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
              return hashA - hashB;
            });
          }
          break;
      }
      return sorted;
    });

    const filteredFocusTags = computed(() => {
      if (!state.searchFocusQuery) return predefinedFocusTags;
      return predefinedFocusTags.filter((tag) =>
        tag.toLowerCase().includes(state.searchFocusQuery.toLowerCase())
      );
    });

    const filteredMediumTags = computed(() => {
      if (!state.searchMediumQuery) return predefinedMediumTags;
      return predefinedMediumTags.filter((tag) =>
        tag.toLowerCase().includes(state.searchMediumQuery.toLowerCase())
      );
    });

    // Helper function to check if a profile is 100% complete
    // checkRole parameter allows checking specific role for "both" users
    const isProfileComplete = (data, checkRole = null) => {
      const role = checkRole || data.role;
      const isScientist = role === 'scientist';
      const profile = isScientist ? (data.scientistProfile || {}) : (data.profile || {});

      const hasName = !!(data.firstName && data.lastName);
      const hasBio = !!(profile.bio && profile.bio.length > 20) || !!(data.bio && data.bio.length > 20) || !!(data.researchBio && data.researchBio.length > 20);
      const hasProfilePhoto = !!(profile.profilePictureURL || data.profilePictureURL);
      const hasBackgroundPhoto = !!(profile.profileBackgroundURL || data.profileBackgroundURL);
      const hasTags = !!(data.tagsSelected && data.tagsSelected.length > 0);

      // Role-specific requirements
      if (isScientist) {
        // Scientists require publications, portfolio is optional
        const hasPublications = !!(data.publications && data.publications.trim());
        return hasName && hasBio && hasProfilePhoto && hasBackgroundPhoto && hasTags && hasPublications;
      } else {
        // Artists require portfolio, awards is optional
        const hasPortfolio = !!(data.portfolio && data.portfolio.length > 0);
        return hasName && hasBio && hasProfilePhoto && hasBackgroundPhoto && hasTags && hasPortfolio;
      }
    };

    // LOAD DATA ONCE - Only called on mount
    const loadData = async () => {
      state.loading = true;
      state.error = null;
      // Generate random seed for stable random sorting
      state.randomSeed = Date.now().toString();

      try {
        const usersCol = collection(db, "users");
        const usersSnapshot = await getDocs(usersCol);

        // Helper to build profile entry for a specific display role
        const buildProfileEntry = (doc, data, displayRole) => {
          const isScientistView = displayRole === 'scientist';
          // Use the correct source profile based on display role
          const sourceProfile = isScientistView
            ? (data.scientistProfile || data.profile || {})
            : (data.profile || {});
          const sourceName = `${data.firstName || ""} ${data.lastName || ""}`.trim();

          // For scientists, use scientist-specific image URLs and positioning
          const profilePicURL = isScientistView
            ? (data.scientistProfilePictureURL || sourceProfile.profilePictureURL || data.profilePictureURL)
            : (sourceProfile.profilePictureURL || data.profilePictureURL);

          const backgroundURL = isScientistView
            ? (data.scientistBackgroundURL || sourceProfile.profileBackgroundURL || data.profileBackgroundURL)
            : (sourceProfile.profileBackgroundURL || data.profileBackgroundURL);

          // Get image positioning based on role
          const profileImageZoom = isScientistView
            ? (data.scientistProfileImageZoom || data.profileImageZoom)
            : data.profileImageZoom;
          const profileImagePositionX = isScientistView
            ? (data.scientistProfileImagePositionX || data.profileImagePositionX)
            : data.profileImagePositionX;
          const profileImagePositionY = isScientistView
            ? (data.scientistProfileImagePositionY || data.profileImagePositionY)
            : data.profileImagePositionY;
          const backgroundImageZoom = isScientistView
            ? (data.scientistBackgroundZoom || data.backgroundImageZoom)
            : data.backgroundImageZoom;
          const backgroundImagePositionX = isScientistView
            ? (data.scientistBackgroundPositionX || data.backgroundImagePositionX)
            : data.backgroundImagePositionX;
          const backgroundImagePositionY = isScientistView
            ? (data.scientistBackgroundPositionY || data.backgroundImagePositionY)
            : data.backgroundImagePositionY;

          const profile = {
            ...sourceProfile,
            name: sourceProfile.name || sourceName,
            profileImageCropShape: data.profileImageCropShape,
            profileImageAspectRatio: data.profileImageAspectRatio,
            profileImageCornerRadius: data.profileImageCornerRadius,
            profileImageZoom: profileImageZoom,
            profileImagePositionX: profileImagePositionX,
            profileImagePositionY: profileImagePositionY,
            backgroundImageZoom: backgroundImageZoom,
            backgroundImagePositionX: backgroundImagePositionX,
            backgroundImagePositionY: backgroundImagePositionY,
            profilePictureURL: profilePicURL,
            profileBackgroundURL: backgroundURL
          };

          // Use role-specific tags if available
          const tags = isScientistView
            ? (data.scientistTags || data.tagsSelected || [])
            : (data.tagsSelected || []);

          return {
            id: doc.id,
            visibleAs: displayRole,
            ...data,
            role: displayRole,
            profile,
            tagsSelected: tags
          };
        };

        const validArtists = [];

        usersSnapshot.docs.forEach((doc) => {
          const data = doc.data();

          // Must be verified
          if (data.verified !== true) return;

          if (data.role === 'both') {
            const artistVis = data.artistVisible !== undefined ? data.artistVisible : true;
            const scientistVis = data.scientistVisible !== undefined ? data.scientistVisible : true;
            const artistComplete = isProfileComplete(data, 'artist');
            const scientistComplete = isProfileComplete(data, 'scientist');

            // Create artist-view entry for artist filter
            if (artistVis && artistComplete) {
              const entry = buildProfileEntry(doc, data, 'artist');
              entry.originalRole = 'both';
              validArtists.push(entry);
            }
            // Create scientist-view entry for scientist filter
            if (scientistVis && scientistComplete) {
              const entry = buildProfileEntry(doc, data, 'scientist');
              entry.originalRole = 'both';
              validArtists.push(entry);
            }
            // Create combined entry for "both" filter (use artist profile as base)
            if (artistVis || scientistVis) {
              const bothEntry = buildProfileEntry(doc, data, artistComplete ? 'artist' : 'scientist');
              bothEntry.role = 'both';
              bothEntry.visibleAs = 'both';
              bothEntry.originalRole = 'both';
              bothEntry.artistComplete = artistComplete;
              bothEntry.scientistComplete = scientistComplete;
              // Store separate tag lists for grouped display
              bothEntry.originalArtTags = data.tagsSelected || [];
              bothEntry.originalSciTags = data.scientistTags || [];
              // Merge tags for search/filter matching
              bothEntry.tagsSelected = [...new Set([...bothEntry.originalArtTags, ...bothEntry.originalSciTags])];
              validArtists.push(bothEntry);
            }
          } else {
            // Single-role users use profileVisible (default to true if undefined)
            const profileVis = data.profileVisible !== undefined ? data.profileVisible : true;
            if (!profileVis) return;
            if (!isProfileComplete(data)) return;
            validArtists.push(buildProfileEntry(doc, data, data.role));
          }
        });

        state.allArtists = validArtists;

        // Initialize Fuse with the FULL list for search
        state.fuse = new Fuse(state.allArtists, {
          keys: ["profile.name", "tagsSelected"],
          threshold: 0.3,
        });

        if (state.allArtists.length === 0) {
          state.error = "No profiles found.";
        }
      } catch (err) {
        console.error(err);
        state.error = "Failed to load profiles. Please try again later.";
      } finally {
        state.loading = false;
      }
    };

    const focusSearchInput = () => {
      if (searchInput.value) {
        searchInput.value.focus();
      }
    };

    const handleTagClick = (tag, type = 'focus') => {
      if (type === 'focus' || predefinedFocusTags.includes(tag)) {
        if (!state.selectedFocus.includes(tag)) {
          state.selectedFocus.push(tag);
          state.dropdowns.focus = true;
        }
      } else if (type === 'medium' || predefinedMediumTags.includes(tag)) {
        if (!state.selectedMedium.includes(tag)) {
          state.selectedMedium.push(tag);
          state.dropdowns.medium = true;
        }
      }
      // No fetch needed - filtering is reactive
    };

    const toggleFocusDropdown = () => {
      state.dropdowns.focus = !state.dropdowns.focus;
    };

    const toggleMediumDropdown = () => {
      state.dropdowns.medium = !state.dropdowns.medium;
    };

    const removeFilter = (filter) => {
      if (filter.type === "focus") {
        state.selectedFocus = state.selectedFocus.filter((t) => t !== filter.tag);
      } else if (filter.type === "medium") {
        state.selectedMedium = state.selectedMedium.filter((t) => t !== filter.tag);
      }
      // No fetch needed - filtering is reactive
    };

    const resetFilters = () => {
      state.selectedFocus = [];
      state.selectedMedium = [];
      state.searchQuery = "";
      state.searchFocusQuery = "";
      state.searchMediumQuery = "";
      state.filterOpenCommissions = false;
      state.error = null;
      // Clear FilterDropdown's internal state
      if (filterDropdownRef.value?.clearAllFilters) {
        filterDropdownRef.value.clearAllFilters();
      }
      // No fetch needed - filtering is reactive
    };

    const clearSearch = () => {
      state.searchQuery = "";
      // No fetch needed - filtering is reactive
    };

    const handleClickOutside = (event) => {
      const isDropdownButton = event.target.closest('.dropdown-button');
      if (isDropdownButton) return;

      const isDropdownContent = event.target.closest('.dropdown-content') ||
                                event.target.closest('.dropdown-item') ||
                                event.target.closest('.custom-checkbox') ||
                                event.target.closest('.checkbox-label') ||
                                event.target.closest('.dropdown-search-input');
      if (isDropdownContent) return;

      const isSortClick = event.target.closest('.sort-container') || event.target.closest('.search-filter-container');
      if (isSortClick) return;

      state.dropdowns.focus = false;
      state.dropdowns.medium = false;
      state.showSortDropdown = false;
    };

    // No more debounced fetch - just update state directly
    const onSearchInput = () => {
      // Filtering happens automatically via computed property
    };

    const onFilterChange = () => {
      // Filtering happens automatically via computed property
    };

    const handleFocusTagClick = (tag) => {
      if (state.selectedFocus.includes(tag)) {
        state.selectedFocus = state.selectedFocus.filter(t => t !== tag);
      } else {
        state.selectedFocus = [...state.selectedFocus, tag];
      }
    };

    const handleMediumTagClick = (tag) => {
      if (state.selectedMedium.includes(tag)) {
        state.selectedMedium = state.selectedMedium.filter(t => t !== tag);
      } else {
        state.selectedMedium = [...state.selectedMedium, tag];
      }
    };

    const toggleSortDropdown = () => {
      state.showSortDropdown = !state.showSortDropdown;
    };

    const selectSortOption = (value) => {
      state.sortOption = value;
      state.showSortDropdown = false;
      localStorage.setItem('pallas-sort-option', value);
      // Generate new random seed when switching to random
      if (value === 'random') {
        state.randomSeed = Date.now().toString();
      }
    };

    const toggleCommissionFilter = () => {
      // Filtering happens automatically via computed property
    };

    const getProfileImageStyle = (profile) => {
      if (!profile.profilePictureURL) {
        return {
          width: '97px',
          height: '97px',
          borderRadius: '50%',
          backgroundColor: '#f0f0f0',
          backgroundImage: `url(${defaultProfile})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      }

      const zoom = profile.profileImageZoom || 1
      const posX = profile.profileImagePositionX !== undefined ? profile.profileImagePositionX : 50
      const posY = profile.profileImagePositionY !== undefined ? profile.profileImagePositionY : 50

      return {
        width: '97px',
        height: '97px',
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        backgroundImage: `url(${profile.profilePictureURL})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: `${posX}% ${posY}%`,
        backgroundRepeat: 'no-repeat'
      }
    }

    const getBackgroundImageStyle = (profile) => {
      const backgroundURL = profile.artShowcaseURL || profile.profileBackgroundURL || defaultBanner

      if (!profile.profileBackgroundURL) {
        return {
          backgroundColor: '#ffffff',
          backgroundImage: `url(${backgroundURL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      }

      // Use the saved zoom value directly - allows zoomed out images with white space
      const zoom = profile.backgroundImageZoom || 1

      return {
        backgroundColor: '#ffffff',
        backgroundImage: `url(${backgroundURL})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: (profile.backgroundImagePositionX !== undefined || profile.backgroundImagePositionY !== undefined)
          ? `${profile.backgroundImagePositionX || 50}% ${profile.backgroundImagePositionY || 50}%`
          : 'center',
        backgroundRepeat: 'no-repeat'
      }
    }

    const handleFilterUpdate = (filters) => {
      // Update type filter
      if (filters.type) state.filterType = filters.type;

      // Update collaboration filter
      state.filterOpenCommissions = filters.collaboration;

      // Update tags
      state.selectedFocus = filters.tags || [];

      // Update sort option
      if (filters.sort && filters.sort !== state.sortOption) {
        state.sortOption = filters.sort;
        localStorage.setItem('pallas-sort-option', filters.sort);
        if (filters.sort === 'random') {
          state.randomSeed = Date.now().toString();
        }
      }
      // No fetch needed - filtering is reactive
    };

    // Helper to convert to Title Case
    const toTitleCase = (str) => {
      if (!str) return '';
      return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    };

    const truncateBio = (bio) => {
      if (!bio) return '';
      if (bio.length <= 50) return bio;
      return bio.substring(0, 50).trim() + '...';
    };

    const getArtistTags = (artist) => {
      const tags = artist.tagsSelected || artist.researchAreas || artist.tags || artist.expertise || [];
      return tags.map(tag => toTitleCase(tag));
    };

    const getArtTags = (artist) => {
      const tags = artist.originalArtTags || artist.tagsSelected || [];
      return tags.map(tag => toTitleCase(tag));
    };

    const getScienceTags = (artist) => {
      const tags = artist.originalSciTags || artist.scientistTags || [];
      return tags.map(tag => toTitleCase(tag));
    };

    const getBothTagsSorted = (artist) => {
      const artTags = getArtTags(artist).map(t => ({ label: t, theme: 'art' }));
      const sciTags = getScienceTags(artist).map(t => ({ label: t, theme: 'science' }));
      // Art tags on left, science tags on right
      return [...artTags, ...sciTags];
    };

    const getTagThemeClass = (tagName) => {
      const theme = getTagTheme(tagName);
      return `tag-${theme === 'science' ? 'science' : theme === 'art' ? 'art' : 'neutral'}`;
    };

    const getRoleBasedBorderClass = (artist) => {
      const role = artist.role || 'both';
      if (role === 'artist') return 'artist-card-art-border';
      if (role === 'scientist') return 'artist-card-science-border';
      return 'artist-card-neutral-border';
    };

    const getProfileBtnClass = (artist) => {
      const role = artist.role || 'both';
      if (role === 'artist') return 'profile-btn-art';
      if (role === 'scientist') return 'profile-btn-science';
      return 'profile-btn-both';
    };

    // Get affiliation for display on card (organization for artists, institution for scientists)
    const getAffiliation = (artist) => {
      const role = artist.role || 'both';
      if (role === 'artist') {
        return artist.artistOrganization || artist.profile?.artistOrganization || null;
      }
      if (role === 'scientist') {
        return artist.institution || artist.profile?.institution || null;
      }
      // For "both", prefer artist organization, fall back to institution
      return artist.artistOrganization || artist.profile?.artistOrganization ||
             artist.institution || artist.profile?.institution || null;
    };

    const getAffiliationClass = (artist) => {
      const role = artist.role || 'both';
      if (role === 'scientist') return 'affiliation-science';
      return 'affiliation-art';
    };

    const focusMainContent = () => {
      if (mainContent.value) {
        mainContent.value.focus();
      }
    };

    const restoreSearchState = () => {
      if (route.query.searchQuery) {
        state.searchQuery = route.query.searchQuery;
      }
      if (route.query.selectedFocus) {
        try {
          const focus = JSON.parse(route.query.selectedFocus);
          if (Array.isArray(focus)) state.selectedFocus = focus;
        } catch (e) {
          console.error('Error parsing selectedFocus:', e);
        }
      }
      if (route.query.selectedMedium) {
        try {
          const medium = JSON.parse(route.query.selectedMedium);
          if (Array.isArray(medium)) state.selectedMedium = medium;
        } catch (e) {
          console.error('Error parsing selectedMedium:', e);
        }
      }
    };

    onMounted(() => {
      restoreSearchState();
      loadData();  // Load data ONCE
      document.addEventListener("click", handleClickOutside);
    });

    onActivated(() => {
      restoreSearchState();
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      defaultBanner,
      defaultProfile,
      predefinedFocusTags,
      predefinedMediumTags,
      ...toRefs(state),
      removeFilter,
      resetFilters,
      clearSearch,
      appliedFilters,
      filteredArtists,
      filteredFocusTags,
      filteredMediumTags,
      focusDropdown,
      mediumDropdown,
      searchGroup,
      searchInput,
      mainContent,
      filterDropdownRef,
      onSearchInput,
      onFilterChange,
      handleTagClick,
      focusSearchInput,
      focusMainContent,
      searchStatusMessage,
      sortOptions,
      toggleSortDropdown,
      selectSortOption,
      handleFocusTagClick,
      handleMediumTagClick,
      toggleFocusDropdown,
      toggleMediumDropdown,
      toggleCommissionFilter,
      handleFilterUpdate,
      getArtistTags,
      getArtTags,
      getScienceTags,
      getBothTagsSorted,
      truncateBio,
      getTagThemeClass,
      getRoleBasedBorderClass,
      getProfileBtnClass,
      getAffiliation,
      getAffiliationClass,
      getProfileImageStyle,
      getBackgroundImageStyle,
    };
  },
};
</script>

<style scoped>
/* ===== ACCESSIBILITY IMPROVEMENTS ===== */
.skip-nav {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--sciColor);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  z-index: 1001;
  transition: all 0.3s ease;
}

.skip-nav:focus {
  top: 10px;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.reset-filters-btn {
  margin-top: var(--paddingStandard);
  padding: var(--paddingTight) var(--paddingStandard);
  background: #6b7280;
  color: white;
  border: none;
  border-radius: var(--stdRadius);
  font-size: var(--buttonText);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--stdTransition);
}

.reset-filters-btn:hover,
.reset-filters-btn:focus {
  background: #4b5563;
  transform: translateY(-2px);
  outline: 2px solid #9ca3af;
  outline-offset: 2px;
}

/* ===== MAIN GALLERY CONTAINER ===== */
.gallery-container {
  width: 100%;
  min-height: 100vh;
  padding-top: var(--navbarHeight);
  position: relative;
  z-index: 1;
}

/* ===== GALLERY HEADER ===== */
.gallery-header {
  position: relative;
  width: 100%;
  padding: var(--paddingSpaceous) 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--paddingSpaceous);
}

/* ===== PALLAS WATERMARK (matching header style) ===== */
.pallas-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 420px;
  height: 60px;
  z-index: 1;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
}

.pallas-watermark-text {
  width: 70%;
  height: 60%;
  background: linear-gradient(90deg, #C6A3B6 0%, #AE96BB 100%);
  -webkit-mask: url('data:image/svg+xml,%3Csvg viewBox="0 0 157.18 19.62" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="black"%3E%3Cpath d="m0,19.34V1.51C0,.83.55.28,1.22.28h6.88c3.98,0,6.91,2.36,6.91,6.46s-2.93,6.46-6.91,6.46H3.07v4.92c0,.68-.55,1.22-1.22,1.22H0ZM3.07,3.04v7.39h5.4c2.5,0,3.5-.97,3.5-3.7s-1-3.7-3.5-3.7H3.07Z"%3E%3C/path%3E%3Cpath d="m27.61,19.34L35.04.28h3.23c.71,0,1.35.44,1.61,1.1l6.55,16.82c.21.55-.19,1.14-.78,1.14h-2.11l-.76-1.94c-.19-.48-.54-.88-1-1.12l-6.12-3.26c-1.04-.55-2.32-.08-2.75,1.01l-1.64,4.17c-.27.69-.95,1.15-1.69,1.15h-1.96Zm13.52-6.19l-2.6-6.55-1.22-3.53h-.23l-1.25,3.53-.79,2c-.15.38,0,.8.34,1.01l5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m129.68,18.2l-6.55-16.82c-.26-.66-.9-1.1-1.61-1.1h-3.23l-7.42,19.05h2.09c.75,0,1.42-.46,1.69-1.15l.31-.79c.19-.48.54-.88,1-1.12l6.12-3.26c1.04-.55,2.32-.08,2.75,1.01l2.1,5.32h1.98c.59,0,.99-.6.78-1.14Zm-13.07-5.05l2.6-6.55,1.22-3.53h.23l1.25,3.53.79,2c.15.37,0,.8-.34,1.01l-5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m59.23,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m85.05,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m142.34,14.25c-.05-.71.51-1.31,1.22-1.31h1.73s.02.87.03.99c.03.33.1.63.2.91.48,1.31,1.78,1.97,4.75,1.97s3.84-.77,3.84-2.53-.57-2.36-3.5-2.96l-3.24-.66c-3.27-.62-5.03-2.22-5.03-5.23,0-2.82,2.19-5.43,7.11-5.43,4.39,0,6.58,2.06,7.19,4.71.04.17.07.35.1.53.05.36.08,1.45.08,1.45h-2.36c-.37,0-.67-.29-.69-.65-.01-.28-.04-.58-.06-.72-.02-.13-.06-.25-.09-.37-.42-1.39-1.63-2.12-4.25-2.12-2.82,0-3.95.8-3.95,2.48s.77,2.22,3.38,2.79l3.24.68c3.58.74,5.15,2.53,5.15,5.4s-2.16,5.46-7.02,5.46-7.25-1.95-7.77-4.92c-.02-.09-.03-.25-.04-.45Z"%3E%3C/path%3E%3C/g%3E%3C/svg%3E') center/contain no-repeat;
  mask: url('data:image/svg+xml,%3Csvg viewBox="0 0 157.18 19.62" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="black"%3E%3Cpath d="m0,19.34V1.51C0,.83.55.28,1.22.28h6.88c3.98,0,6.91,2.36,6.91,6.46s-2.93,6.46-6.91,6.46H3.07v4.92c0,.68-.55,1.22-1.22,1.22H0ZM3.07,3.04v7.39h5.4c2.5,0,3.5-.97,3.5-3.7s-1-3.7-3.5-3.7H3.07Z"%3E%3C/path%3E%3Cpath d="m27.61,19.34L35.04.28h3.23c.71,0,1.35.44,1.61,1.1l6.55,16.82c.21.55-.19,1.14-.78,1.14h-2.11l-.76-1.94c-.19-.48-.54-.88-1-1.12l-6.12-3.26c-1.04-.55-2.32-.08-2.75,1.01l-1.64,4.17c-.27.69-.95,1.15-1.69,1.15h-1.96Zm13.52-6.19l-2.6-6.55-1.22-3.53h-.23l-1.25,3.53-.79,2c-.15.38,0,.8.34,1.01l5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m129.68,18.2l-6.55-16.82c-.26-.66-.9-1.1-1.61-1.1h-3.23l-7.42,19.05h2.09c.75,0,1.42-.46,1.69-1.15l.31-.79c.19-.48.54-.88,1-1.12l6.12-3.26c1.04-.55,2.32-.08,2.75,1.01l2.1,5.32h1.98c.59,0,.99-.6.78-1.14Zm-13.07-5.05l2.6-6.55,1.22-3.53h.23l1.25,3.53.79,2c.15.37,0,.8-.34,1.01l-5.75,3.54Z"%3E%3C/path%3E%3Cpath d="m59.23,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m85.05,18.11V.28h1.85c.68,0,1.22.55,1.22,1.22v15.07h8.93c.68,0,1.22.55,1.22,1.22v1.53h-12c-.68,0-1.22-.55-1.22-1.22Z"%3E%3C/path%3E%3Cpath d="m142.34,14.25c-.05-.71.51-1.31,1.22-1.31h1.73s.02.87.03.99c.03.33.1.63.2.91.48,1.31,1.78,1.97,4.75,1.97s3.84-.77,3.84-2.53-.57-2.36-3.5-2.96l-3.24-.66c-3.27-.62-5.03-2.22-5.03-5.23,0-2.82,2.19-5.43,7.11-5.43,4.39,0,6.58,2.06,7.19,4.71.04.17.07.35.1.53.05.36.08,1.45.08,1.45h-2.36c-.37,0-.67-.29-.69-.65-.01-.28-.04-.58-.06-.72-.02-.13-.06-.25-.09-.37-.42-1.39-1.63-2.12-4.25-2.12-2.82,0-3.95.8-3.95,2.48s.77,2.22,3.38,2.79l3.24.68c3.58.74,5.15,2.53,5.15,5.4s-2.16,5.46-7.02,5.46-7.25-1.95-7.77-4.92c-.02-.09-.03-.25-.04-.45Z"%3E%3C/path%3E%3C/g%3E%3C/svg%3E') center/contain no-repeat;
  opacity: 0.3;
}

.gallery-title {
  font-size: 3.5rem;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-primary);
  color: var(--headersColor);
  text-align: center;
  margin: 0 0 calc(var(--paddingSpaceous) * 1.5) 0;
  z-index: 10;
  position: relative;
  letter-spacing: -0.02em;
}
/* ===== SEARCH AND FILTER CONTROLS ===== */
.gallery-search-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 900px;
  z-index: 10;
  position: relative;
  margin-bottom: var(--paddingSpaceous);
}

.search-section {
  width: 100%;
  max-width: none;
}

.search-form {
  width: 100%;
  position: relative;
}

.search-input-container {
  position: relative;
  background: white;
  border-radius: 10px;
  border: 1px solid var(--borderColor);
  transition: all var(--stdTransition);
  display: flex;
  align-items: center;
  width: 100%;
}

.search-input-container::before {
  content: '';
  position: absolute;
  inset: -1px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-radius: 10px;
  z-index: -1;
  opacity: 0;
  transition: all var(--stdTransition);
}

.search-input-container:focus-within {
  border-color: transparent;
}

.search-input-container:focus-within::before {
  opacity: 1;
}

.filter-icon-section {
  padding: var(--paddingTight);
  border-right: 1px solid var(--borderColor);
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  padding: var(--paddingTight) var(--paddingSpaceous);
  border: none;
  border-radius: 0;
  font-size: var(--buttonText);
  font-family: var(--font-primary);
  color: var(--textColor);
  background: transparent;
  outline: none;
  transition: all var(--stdTransition);
}

.search-input::placeholder {
  color: var(--textColor);
  opacity: 0.6;
}

.search-clear-btn {
  padding: var(--paddingTight);
  margin: 0 var(--paddingTight) 0 0;
  background: transparent;
  border: none;
  font-size: var(--labelText);
  color: var(--textColor);
  cursor: pointer;
  opacity: 0.6;
  transition: all var(--stdTransition);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.search-clear-btn:hover {
  opacity: 1;
  background: var(--neutralColor);
}

.filter-section {
  flex-shrink: 0;
}
/* ===== ACTIVE FILTERS SECTION - REMOVED ===== */
/* Active filters now handled within FilterDropdown component */

.sort-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #d1d5db;
  z-index: 1000;
  min-width: 200px;
  max-width: 280px;
  font-family: inherit;
}
.sort-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: pointer;
  color: #374151;
  font-size: 14px;
  font-weight: 400;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f3f4f6;
  position: relative;
}
.sort-option:first-child {
  border-radius: 4px 4px 0 0;
}
.sort-option:last-child {
  border-bottom: none;
  border-radius: 0 0 4px 4px;
}
.sort-option:hover {
  background: #f9fafb;
  color: #111827;
}
.sort-option.active {
  background: #f3f4f6;
  color: #111827;
  font-weight: 500;
}
.sort-option.active::after {
  content: '✓';
  color: #6567BE;
  font-weight: 600;
  font-size: 16px;
}
.selected-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
  flex-grow: 1;
}
.selected-tag {
  margin-top: 15px;
  margin-bottom: 25px;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}
.selected-focus-tag {
  background: #f1f5f9;
  color: #475569;
  border-color: #64748b;
}
.selected-focus-tag:hover {
  background: #e2e8f0;
  color: #334155;
  border-color: #475569;
}
.selected-medium-tag {
  background: #f3f4ff;
  color: #6366f1;
  border-color: #a5b4fc;
}
.selected-medium-tag:hover {
  background: #e0e7ff;
  color: #4338ca;
  border-color: #818cf8;
}
.tag-remove {
  margin-left: 8px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}
.tag-remove:hover {
  opacity: 1;
}
.selected-focus-tag:hover .tag-remove,
.selected-medium-tag:hover .tag-remove {
  color: inherit !important;
}

/* ===== RESULTS SECTION ===== */
.results {
  width: 100%;
  padding: var(--paddingSpaceous) var(--paddingStandard);
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ===== LOADING/ERROR/NO RESULTS STATES ===== */
.loading,
.error-message,
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--paddingStandard);
  text-align: center;
  font-family: var(--font-primary);
  color: var(--textColor);
  padding: var(--paddingSpaceous);
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  box-shadow: var(--stdShadow);
  border: var(--stdOutline) solid var(--borderColor);
  max-width: 500px;
  margin: var(--paddingSpaceous) auto;
}

.loading {
  border-color: var(--artOutlineColor);
}

.error-message {
  border-color: #9ca3af;
  background: #f9fafb;
}

.no-results {
  border-color: #d1d5db;
  border-width: 1px;
  background: #f9fafb;
}

.loading::before {
  content: '';
  width: 40px;
  height: 40px;
  border: 4px solid var(--artOutlineColor);
  border-top: 4px solid var(--artColor);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  will-change: transform;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Respect user's motion preferences */
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
  }
}

.error-message {
  font-size: var(--buttonText);
}

.no-results {
  font-size: var(--buttonText);
}

.loading {
  font-size: var(--buttonText);
  font-weight: var(--font-weight-bold);
}
/* ===== ARTIST GRID ===== */
.artist-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--paddingStandard);
  width: 100%;
  max-width: 1200px;
  margin: var(--paddingSpaceous) auto;
  padding: 0 var(--paddingStandard);
}

/* ===== ARTIST CARDS ===== */
.pallas-artist-card {
  width: 100%;
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  overflow: hidden;
  transition: all var(--stdTransition);
  border: 2px solid var(--borderColor);
  position: relative;
}

/* Role-based border colors */
.artist-card-art-border {
  border-color: var(--artOutlineColor);
}

.artist-card-science-border {
  border-color: var(--sciOutlineColor);
}

.artist-card-neutral-border {
  border-color: var(--borderColor);
}

.pallas-artist-card::before {
  content: '';
  position: absolute;
  inset: -1px;
  background: linear-gradient(45deg, var(--artOutlineColor) 0%, var(--sciOutlineColor) 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
  opacity: 0;
  transition: all var(--stdTransition);
}

.pallas-artist-card:hover {
  /* Subtle lift with light shadow on hover only */
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* .pallas-artist-card:hover::before {
  Remove gradient border on hover
  opacity: 1;
} */
.art-hero-main {
  width: 100%;
  height: 140px;
  background-size: cover;
  background-position: center;
  position: relative;
  background-color: #f3f4f6;
}
.commission-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, var(--artColor), var(--sciColor));
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 0.9;
}
.commission-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  will-change: transform, opacity;
}
.commission-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.2);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .commission-dot {
    animation: none;
  }
}

.profile-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: var(--bckColor);
  border: 2px solid var(--borderColor);
  border-radius: var(--stdRadius);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-decoration: none;
  margin-top: var(--paddingTight);
  align-self: flex-start;
  color: var(--textColor);
}

.profile-btn .btn-text {
  color: var(--textColor);
}

.profile-btn .icon {
  width: 16px;
  height: 16px;
}

.profile-btn .icon path {
  stroke: var(--textColor);
}

.profile-btn:hover,
.profile-btn:focus {
  transform: translateY(-2px);
  outline: none;
}

/* Artist role button */
.profile-btn-art {
  border-color: var(--artOutlineColor);
}

.profile-btn-art .btn-text,
.profile-btn-art .icon path {
  color: var(--artColor);
  stroke: var(--artColor);
}

.profile-btn-art:hover,
.profile-btn-art:focus {
  border-color: var(--artColor);
  box-shadow: 0 4px 12px rgba(248, 177, 155, 0.3);
}

/* Scientist role button */
.profile-btn-science {
  border-color: var(--sciOutlineColor);
}

.profile-btn-science .btn-text,
.profile-btn-science .icon path {
  color: var(--sciColor);
  stroke: var(--sciColor);
}

.profile-btn-science:hover,
.profile-btn-science:focus {
  border-color: var(--sciColor);
  box-shadow: 0 4px 12px rgba(101, 102, 190, 0.3);
}

/* Both role button */
.profile-btn-both {
  border-color: var(--blendColor);
}

.profile-btn-both .btn-text {
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.profile-btn-both .icon path {
  stroke: var(--blendColor);
}

.profile-btn-both:hover,
.profile-btn-both:focus {
  border-color: var(--blendColor);
  box-shadow: 0 4px 12px rgba(175, 140, 173, 0.3);
}
.artist-profile-section {
  display: flex;
  gap: 10px;
  align-items: flex-end; /* Align name to bottom of profile image */
  padding: 10px 10px 12px 10px;
  position: relative;
  margin-top: -40px; /* Creates overlap with banner */
}
.profile-image-container {
  position: relative;
  flex-shrink: 0;
  overflow: hidden; /* CRITICAL: Enables cropping via CSS transforms */
  z-index: 1; /* Ensure image is above the banner */
}
.profile-image-main {
  /* Dimensions set directly by JavaScript - no CSS variables needed */
  object-fit: cover;
  object-position: center;
  border: 4px solid white;
  background-color: #f3f4f6;
  display: block;
  flex-shrink: 0;
}
.artist-info-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding-top: 40px;
  padding-right: 0;
}
.name-affiliation-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  min-width: 0;
  margin-bottom: 8px;
}
.artist-name-main {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
  letter-spacing: -0.01em;
}
.dual-profile-btns {
  display: flex;
  gap: 8px;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.dual-profile-btns .profile-btn-compact {
  position: static;
  padding: 4px 10px;
  font-size: 0.7rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #555;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.dual-profile-btns .profile-btn-art .icon path {
  stroke: var(--artColor);
}

.dual-profile-btns .profile-btn-science .icon path {
  stroke: var(--sciColor);
}

.dual-profile-btns .profile-btn-art:hover {
  color: var(--artColor);
}

.dual-profile-btns .profile-btn-science:hover {
  color: var(--sciColor);
}

.btn-disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: auto;
}

.btn-disabled:hover {
  transform: none;
  filter: none;
}

.role-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.role-badge-artist,
.role-badge-scientist,
.role-badge-both {
  background: #f0f0f0;
  color: #666;
}

.card-affiliation {
  font-size: 0.8rem;
  font-weight: 400;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.card-tags-section {
  padding: 6px 14px 12px 14px;
}
.tag-group-bottom {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-content: flex-start;
}
.artist-title-main {
  margin: 0 0 12px 0;
  font-size: 0.95rem;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.4;
  align-self: flex-start;
}
/* Old expertise-tags-main removed - tags now inline with name */
.expertise-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: var(--paddingTight) 8px;
  border-radius: var(--stdRadius);
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  font-family: var(--font-primary);
  transition: all var(--stdTransition);
  border: var(--stdOutline) solid transparent;
  cursor: default;
  text-transform: none;
  letter-spacing: normal;
  line-height: 1;
  background: none;
  outline: none;
  text-decoration: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.expertise-tag.clickable-tag {
  cursor: pointer;
}

/* Art-themed tags - matching FilterDropdown inactive styling */
.art-tag {
  background: var(--bckColor);
  border-color: var(--artOutlineColor);
  color: var(--artColor);
}

/* Science-themed tags - matching FilterDropdown inactive styling */
.research-tag {
  background: var(--bckColor);
  border-color: var(--sciOutlineColor);
  color: var(--sciColor);
}

/* Hover effects for clickable tags */
.expertise-tag.clickable-tag:hover {
  transform: var(--hoverLift);
  box-shadow: var(--stdShadow);
}

.art-tag.clickable-tag:hover {
  background: var(--artOutlineColor);
  border-color: var(--artColor);
  color: var(--artColor);
}

/* Theme-based tag styles to match edit view */
.tag-science {
  background: rgba(101, 102, 190, 0.1);
  border: 1px solid rgba(101, 102, 190, 0.3);
  color: #6566be;
}

.tag-art {
  background: rgba(248, 177, 155, 0.1);
  border: 1px solid rgba(248, 177, 155, 0.3);
  color: #f8b19b;
}

.tag-neutral {
  background: rgba(209, 210, 230, 0.1);
  border: 1px solid rgba(209, 210, 230, 0.3);
  color: #666;
}

.research-tag.clickable-tag:hover {
  background: var(--sciOutlineColor);
  border-color: var(--sciColor);
  color: var(--sciColor);
}

/* Compact profile button in top right of card */
.profile-btn-compact {
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--textColor);
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  z-index: 2;
}

.profile-btn-compact:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.profile-btn-compact .icon {
  width: 14px;
  height: 14px;
  stroke: currentColor;
}

.profile-btn-compact {
  border-radius: 6px; /* Less rounded corners */
}

.profile-btn-compact.profile-btn-art,
.profile-btn-compact.profile-btn-science,
.profile-btn-compact.profile-btn-neutral,
.profile-btn-compact.profile-btn-both {
  color: #555; /* Grey for all buttons */
}

.profile-btn-compact.profile-btn-art .btn-text,
.profile-btn-compact.profile-btn-science .btn-text,
.profile-btn-compact.profile-btn-neutral .btn-text,
.profile-btn-compact.profile-btn-both .btn-text {
  color: #555; /* Grey text for all compact buttons */
  background: none;
  -webkit-background-clip: unset;
  background-clip: unset;
}

.profile-btn-compact .icon path {
  stroke: #555; /* Grey arrow */
}

/* Card tag pills - compact styling matching dropdown */
.card-tag-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: var(--bckColor);
  border: var(--stdOutline) solid var(--borderColor);
  border-radius: 4px;
  font-size: 11px;
  font-family: var(--font-primary);
  color: #555;
  white-space: nowrap;
}

.card-tag-pill[data-theme="art"] {
  background: var(--bckColor);
  border-color: var(--artOutlineColor);
  color: #555;
}

.card-tag-pill[data-theme="science"] {
  background: var(--bckColor);
  border-color: var(--sciOutlineColor);
  color: #555;
}

.card-tag-pill[data-theme="neutral"] {
  background: var(--bckColor);
  border-color: var(--borderColor);
  color: #555;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}
.slide-enter-from {
  max-height: 0;
  opacity: 0;
  transform: scaleY(0);
}
.slide-enter-to {
  max-height: 300px;
  opacity: 1;
  transform: scaleY(1);
}
.slide-leave-from {
  max-height: 300px;
  opacity: 1;
  transform: scaleY(1);
}
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: scaleY(0);
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: var(--breakpointTablet)) {
  .gallery-container {
    padding-top: calc(var(--navbarHeight) * 0.8);
  }
  
  .gallery-header {
    padding: var(--paddingStandard) var(--paddingStandard) var(--paddingSpaceous) var(--paddingStandard);
  }
  
  .pallas-watermark {
    width: 280px;
    height: 42px;
  }
  
  .gallery-title {
    font-size: calc(var(--title) * 0.9);
  }
  
  .gallery-search-controls {
    flex-direction: column;
    gap: var(--paddingStandard);
    width: 100%;
    padding: 0 var(--paddingStandard);
  }
  
  .search-section {
    width: 100%;
    max-width: none;
  }
  
  .filter-section {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  
  /* .active-filters-section {
    padding: var(--paddingStandard);
  } */
  
  .artist-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: var(--paddingStandard);
  }
  
  .artist-profile-section {
    padding: var(--paddingStandard) var(--paddingStandard) var(--paddingTight) var(--paddingStandard);
    gap: var(--paddingStandard);
  }
  
  /* .profile-image-main dimensions set directly by JavaScript getProfileImageStyle() */
  
  .artist-name-main {
    font-size: 1.75rem;
  }

  .card-affiliation {
    font-size: 0.75rem;
  }

  .artist-title-main {
    font-size: var(--labelText);
  }
  
  .expertise-tags-main {
    padding: 0 var(--paddingStandard) var(--paddingStandard) var(--paddingStandard);
  }
}

@media (max-width: 600px) {
  .gallery-search-controls {
    padding: 0 var(--paddingTight);
  }

  .results {
    padding: var(--paddingStandard) var(--paddingTight);
  }

  .artist-grid {
    grid-template-columns: 1fr;
    padding: 0;
  }

  .pallas-watermark {
    width: 220px;
    height: 32px;
  }

  .gallery-title {
    font-size: calc(var(--title) * 0.8);
  }

  .artist-profile-section {
    flex-direction: column;
    text-align: center;
    gap: var(--paddingTight);
  }

  .profile-image-container {
    align-self: center;
  }

  .artist-info-main {
    align-items: center;
  }

  .name-affiliation-row {
    align-items: center;
  }

  .artist-name-main,
  .artist-title-main {
    text-align: center;
  }

  .profile-btn {
    align-self: center;
    min-height: 44px; /* Touch target size */
    padding: 0.75rem 1.5rem;
  }

  .search-input {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 12px var(--paddingStandard);
  }

  .search-clear-btn {
    min-width: 44px;
    min-height: 44px;
  }

  /* Enhanced touch targets for mobile */
  .pallas-artist-card {
    transition: all 0.2s ease;
  }

  .pallas-artist-card:focus-within {
    outline: 2px solid var(--sciColor);
    outline-offset: 2px;
  }

  /* Larger text for better mobile readability */
  .artist-name-main {
    font-size: 1.5rem;
  }

  .card-affiliation {
    font-size: 0.75rem;
  }

  .expertise-tag {
    padding: var(--paddingTight) 10px;
    font-size: 14px;
    min-height: 32px;
    display: flex;
    align-items: center;
  }
}

/* Enhanced accessibility and keyboard navigation */
.pallas-artist-card:focus-within {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.search-input:focus {
  outline: none;
  border-color: transparent;
}

.search-input-container:focus-within {
  box-shadow: 0 0 0 2px var(--sciColor);
}

/* Loading state accessibility */
.loading::after {
  content: 'Loading artist profiles...';
  font-size: var(--buttonText);
  margin-top: var(--paddingStandard);
}

/* Error and no-results states */
.error-message,
.no-results {
  max-width: 600px;
  text-align: center;
}

.no-results p {
  margin: 0 0 var(--paddingStandard) 0;
}

</style>